import { useState, useEffect } from "react";
import { listScenarios, getRiskAssessment, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, Loader2, AlertTriangle, CheckCircle, XCircle, Landmark, TrendingUp, Building2, LogOut, Sparkles } from "lucide-react";

const SCORE_CONFIG = {
  GREEN: { bg: "hsl(142 60% 94%)", border: "hsl(142 60% 40%)", text: "hsl(142 60% 25%)", icon: CheckCircle, label: "Low Risk" },
  AMBER: { bg: "hsl(38 92% 92%)", border: "hsl(38 92% 50%)", text: "hsl(38 70% 30%)", icon: AlertTriangle, label: "Moderate Risk" },
  RED: { bg: "hsl(0 72% 94%)", border: "hsl(0 72% 50%)", text: "hsl(0 72% 30%)", icon: XCircle, label: "High Risk" },
};

const CATEGORY_ICONS = {
  "Planning Risk": Landmark,
  "Market Risk": TrendingUp,
  "Financial Risk": Building2,
  "Exit Risk": LogOut,
};

export default function RiskMatrix() {
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [riskData, setRiskData] = useState(null);

  useEffect(() => {
    listScenarios().then(setScenarios).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedId) {
      const s = scenarios.find((s) => s.id === selectedId);
      if (s?.location) setPostcode(s.location);
    }
  }, [selectedId, scenarios]);

  const runAssessment = async () => {
    if (!selectedId || !postcode.trim()) return;
    setLoading(true);
    setRiskData(null);
    try {
      const res = await getRiskAssessment(selectedId, postcode.trim());
      if (res.success) {
        setRiskData(res.data);
        toast.success("Risk assessment complete");
      }
    } catch (e) {
      toast.error("Failed to run risk assessment");
    } finally {
      setLoading(false);
    }
  };

  const overall = riskData ? SCORE_CONFIG[riskData.overall_score] || SCORE_CONFIG.AMBER : null;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, fontFamily: "Manrope", letterSpacing: "-0.02em" }}>
        Risk Matrix
      </h1>
      <p style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", marginTop: 4, marginBottom: 28 }}>
        AI-powered investment readiness assessment
      </p>

      <div style={{ display: "flex", gap: 12, marginBottom: 28, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ minWidth: 280 }}>
          <label style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>Scenario</label>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="risk-scenario-select">
              <SelectValue placeholder="Select a scenario" />
            </SelectTrigger>
            <SelectContent>
              {scenarios.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.project_name || s.id.slice(0, 8)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div style={{ minWidth: 200 }}>
          <label style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>Postcode</label>
          <Input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="E14 9SH"
            style={{ fontFamily: "JetBrains Mono" }}
            data-testid="risk-postcode-input"
          />
        </div>
        <Button
          onClick={runAssessment}
          disabled={loading || !selectedId || !postcode.trim()}
          style={{ background: "hsl(var(--foreground))", color: "white", minWidth: 160 }}
          data-testid="risk-assess-btn"
        >
          {loading ? <><Loader2 size={16} className="animate-spin" /><span style={{ marginLeft: 8 }}>Analyzing...</span></> : <><Shield size={16} /><span style={{ marginLeft: 8 }}>Assess Risk</span></>}
        </Button>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "80px 20px", color: "hsl(var(--muted-foreground))" }}>
          <Loader2 size={32} className="animate-spin" style={{ margin: "0 auto 16px" }} />
          <p style={{ fontSize: 15, fontWeight: 500 }}>Running risk assessment...</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Analyzing planning, market, financial, and exit risks</p>
        </div>
      )}

      {!riskData && !loading && (
        <div style={{ textAlign: "center", padding: "80px 20px", color: "hsl(var(--muted-foreground))" }}>
          <Shield size={48} style={{ margin: "0 auto 16px", opacity: 0.2 }} />
          <p style={{ fontSize: 15, fontWeight: 500 }}>Select a scenario and postcode to assess</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>AI will evaluate planning, market, financial, and exit risks</p>
        </div>
      )}

      {riskData && !riskData.raw_analysis && (
        <div className="animate-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Overall Score */}
          {overall && (
            <Card style={{ border: `2px solid ${overall.border}`, background: overall.bg }} data-testid="overall-score-card">
              <CardContent style={{ padding: "24px 28px", display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, background: overall.border, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <overall.icon size={32} style={{ color: "white" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, fontFamily: "Manrope", color: overall.text }} data-testid="overall-score">
                      {riskData.overall_score}
                    </span>
                    <span style={{ fontSize: 14, color: overall.text, fontWeight: 500 }}>{overall.label}</span>
                    {riskData.investment_readiness && (
                      <span style={{ fontSize: 12, background: "white", padding: "2px 10px", borderRadius: 12, fontWeight: 600, color: overall.text }}>
                        {riskData.investment_readiness}
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 14, color: overall.text, lineHeight: 1.6 }}>{riskData.overall_summary}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Category Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
            {(riskData.categories || []).map((cat, i) => {
              const cfg = SCORE_CONFIG[cat.score] || SCORE_CONFIG.AMBER;
              const Icon = CATEGORY_ICONS[cat.name] || Shield;
              return (
                <Card key={i} style={{ borderLeft: `4px solid ${cfg.border}` }} data-testid={`risk-card-${cat.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <CardHeader style={{ paddingBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon size={16} style={{ color: cfg.border }} />
                        {cat.name}
                      </CardTitle>
                      <span style={{ fontSize: 12, fontWeight: 700, background: cfg.bg, color: cfg.text, padding: "3px 12px", borderRadius: 8, border: `1px solid ${cfg.border}` }}>
                        {cat.score}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {cat.key_metric && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: cfg.text, marginBottom: 8, background: cfg.bg, padding: "4px 10px", borderRadius: 6, display: "inline-block" }}>
                        {cat.key_metric}
                      </div>
                    )}
                    <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", lineHeight: 1.6, marginBottom: 10 }}>{cat.reasoning}</p>
                    {cat.mitigations?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Mitigations</div>
                        {cat.mitigations.map((m, j) => (
                          <div key={j} style={{ fontSize: 12, color: "hsl(var(--foreground))", padding: "2px 0", display: "flex", gap: 6 }}>
                            <span style={{ color: cfg.border }}>-</span> {m}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Strengths & Concerns */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {riskData.key_strengths?.length > 0 && (
              <Card data-testid="key-strengths">
                <CardContent style={{ padding: "16px 20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(142 60% 40%)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <Sparkles size={13} /> Key Strengths
                  </div>
                  {riskData.key_strengths.map((s, i) => (
                    <div key={i} style={{ fontSize: 13, color: "hsl(var(--foreground))", padding: "3px 0" }}>+ {s}</div>
                  ))}
                </CardContent>
              </Card>
            )}
            {riskData.key_concerns?.length > 0 && (
              <Card data-testid="key-concerns">
                <CardContent style={{ padding: "16px 20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--destructive))", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertTriangle size={13} /> Key Concerns
                  </div>
                  {riskData.key_concerns.map((c, i) => (
                    <div key={i} style={{ fontSize: 13, color: "hsl(var(--foreground))", padding: "3px 0" }}>- {c}</div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {riskData?.raw_analysis && (
        <Card>
          <CardContent style={{ padding: 24, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6 }}>
            {riskData.raw_analysis}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
